"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Menu, Phone, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { mainNav } from "@/config/navigation";
import { site } from "@/config/site";

export function MobileMenu() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const panel = panelRef.current;
    const trigger = triggerRef.current;
    document.body.style.overflow = "hidden";

    const getFocusable = () =>
      Array.from(
        panel?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea',
        ) ?? [],
      );

    getFocusable()[0]?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
      trigger?.focus();
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls="mobile-menu"
        aria-label={open ? "Zavřít menu" : "Otevřít menu"}
        className="inline-flex h-11 w-11 items-center justify-center text-ink lg:hidden"
      >
        {open ? (
          <X aria-hidden="true" strokeWidth={1.5} className="h-6 w-6" />
        ) : (
          <Menu aria-hidden="true" strokeWidth={1.5} className="h-6 w-6" />
        )}
      </button>

      {open ? (
        <div
          id="mobile-menu"
          ref={panelRef}
          className="fixed inset-x-0 bottom-0 top-[var(--header-h)] z-40 overflow-y-auto bg-paper lg:hidden"
        >
          <nav aria-label="Mobilní navigace" className="container-ledger py-6">
            <ul className="border-y border-line divide-y divide-line">
              {mainNav.map((item) => (
                <li key={item.label}>
                  {item.items ? (
                    <div className="py-4">
                      <Link
                        href={item.href ?? "/sluzby"}
                        onClick={() => setOpen(false)}
                        className="type-h3 block text-ink no-underline"
                      >
                        {item.label}
                      </Link>
                      <ul className="mt-3 flex flex-col gap-3 pl-4">
                        {item.items.map((sub) => (
                          <li key={sub.href}>
                            <Link
                              href={sub.href}
                              onClick={() => setOpen(false)}
                              className="text-[1.0625rem] text-ink-soft no-underline"
                            >
                              {sub.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <Link
                      href={item.href ?? "/"}
                      onClick={() => setOpen(false)}
                      className="type-h3 block py-4 text-ink no-underline"
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col gap-5">
              <a
                href={`tel:${site.phoneHref}`}
                className="type-fact inline-flex items-center gap-2 text-ink no-underline"
              >
                <Phone aria-hidden="true" strokeWidth={1.5} className="h-5 w-5" />
                {site.phone}
              </a>
              <Button href="/kontakt" withArrow className="w-full">
                Domluvit konzultaci
              </Button>
            </div>
          </nav>
        </div>
      ) : null}
    </>
  );
}
